/*
    This component is tested in Message.stories.tsx

     * BotMessageWithSources
     * BotMessageWithSourcesValar
     * BotMessageWithTitlesWrapping
*/
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type LinkToLessonInStreamWithContentAccessInfo } from 'Lessons';
import { type DisplayableNonLessonFrameSourceLocation, type SourceLink } from '../../types/AnswerSource.types';
import {
    isDisplayableNonLessonFrameSourceLocation,
    isLinkToLessonInStreamWithContentAccessInfo,
} from '../../utils/sourceLocationHelpers';
import LessonLinkRow from './LessonLinkRow';
import NonLessonLinkRow from './NonLessonLinkRow';
import { isFirstLessonLinkInStream, isFirstLinkFromHelpScoutCollection } from './shared';

type Props = { sourceLinks: SourceLink[] };

export default function MessageSources({ sourceLinks }: Props) {
    const { t } = useTranslation('back_royal');
    const {
        lessonLinks,
        nonLessonLinks,
    }: {
        lessonLinks: LinkToLessonInStreamWithContentAccessInfo[];
        nonLessonLinks: DisplayableNonLessonFrameSourceLocation[];
    } = useMemo(() => {
        const _lessonLinks: LinkToLessonInStreamWithContentAccessInfo[] = [];
        const _nonLessonLinks: DisplayableNonLessonFrameSourceLocation[] = [];
        sourceLinks.forEach(sourceLink => {
            if (isLinkToLessonInStreamWithContentAccessInfo(sourceLink.link)) {
                _lessonLinks.push(sourceLink.link as LinkToLessonInStreamWithContentAccessInfo);
            }
            if (isDisplayableNonLessonFrameSourceLocation(sourceLink.link)) {
                _nonLessonLinks.push(sourceLink.link as DisplayableNonLessonFrameSourceLocation);
            }
        });
        return { lessonLinks: _lessonLinks, nonLessonLinks: _nonLessonLinks };
    }, [sourceLinks]);

    return (
        <>
            <hr className="mb-3.5 mt-5 bg-beige-medium" />
            <div className="flex gap-[10px] leading-4 max-sm:flex-wrap">
                <div className="mb-2 shrink-0 grow-0 basis-[max-content] font-medium">{t('chat.chat.learn_more')}</div>
                <div className="grid grid-cols-[fit-content(60%)_auto] items-start gap-y-2 font-semibold grid-areas-[left-col_right-col] max-sm:grid-cols-[auto] max-sm:grid-areas-[left-col,right-col]">
                    {lessonLinks.map(lessonLink => (
                        <LessonLinkRow
                            key={lessonLink.lessonLaunchPath}
                            lessonLink={lessonLink}
                            isFirstInStream={isFirstLessonLinkInStream(lessonLink, lessonLinks)}
                            isFirstRow={sourceLinks[0].link === lessonLink}
                            canLaunch={lessonLink.canLaunch}
                            reasonMessage={lessonLink.reasonMessage}
                        />
                    ))}

                    {nonLessonLinks.map(nonLessonLink => (
                        <NonLessonLinkRow
                            key={nonLessonLink.url}
                            nonLessonLink={nonLessonLink}
                            isFirstInHelpScoutCollection={isFirstLinkFromHelpScoutCollection(
                                nonLessonLink,
                                nonLessonLinks,
                            )}
                            isFirstRow={sourceLinks[0].link === nonLessonLink}
                        />
                    ))}
                </div>
            </div>
        </>
    );
}
